import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence } from 'framer-motion';
import { Loader2, Package, Plus } from 'lucide-react';

import { AddActionButton } from 'components/AddActionButton';
import { Button } from 'components/Button';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { getCommodities } from 'lib/api/getCommodities';
import { getCommodityDetails } from 'lib/api/getCommodityDetails';
import CommodityCreatorDialog from 'pages/QuoteView/LoadBuilding/GlobalTranzSectionForms/components/CommodityCreatorDialog';
import { CommodityItemCard } from 'pages/QuoteView/LoadBuilding/GlobalTranzSectionForms/components/CommodityItemCard';
import {
  NormalizedLoad,
  TMSCommodity,
  TMSCustomer,
  Unit,
  createNewCommodity,
} from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import {
  GenericCompanySearchableFields,
  mapCommoditiesToAntdOptions,
} from 'utils/loadInfoAndBuilding';

import {
  handleCommodityOrLocationFetchError,
  mapCommodityDefaults,
} from './utils';

interface ShipmentItemsProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  commodities: Maybe<TMSCommodity[]>;
  setCommodities: React.Dispatch<React.SetStateAction<Maybe<TMSCommodity[]>>>;
  isLoadingCommodities: boolean;
  setIsLoadingCommodities: React.Dispatch<React.SetStateAction<boolean>>;
  tmsID?: number;
  customerID?: number; // Selected customer's database ID
  customerBK?: string; // Selected customer's externalTMSID (for API calls)
  carrierType?: number; // For filtering commodities by carrier type (TL = 3)
  /** Customer list from main form so Create Commodity modal uses same list as Customer dropdown */
  initialCustomers?: Maybe<TMSCustomer[]>;
  initialIsLoadingCustomers?: boolean;
}

export function ShipmentItems({
  formMethods,
  commodities,
  setCommodities,
  isLoadingCommodities,
  setIsLoadingCommodities,
  carrierType,
  tmsID,
  customerID,
  customerBK,
  initialCustomers,
  initialIsLoadingCustomers,
}: ShipmentItemsProps) {
  const {
    control,
    formState: { errors },
  } = formMethods;
  const { toast } = useToast();
  const [selectedCommodityID, setSelectedCommodityID] =
    useState<Maybe<string>>(null);
  const [selectedCommodityValue, setSelectedCommodityValue] =
    useState<any>(null);
  const [isLoadingCommodityDetails, setIsLoadingCommodityDetails] = useState<{
    [key: string]: boolean;
  }>({});
  const [showCommodityDropdown, setShowCommodityDropdown] = useState(false);
  const [isCommodityCreatorOpen, setIsCommodityCreatorOpen] = useState(false);

  // Use field array to manage multiple shipment items
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'commodities',
  });

  // Track previous customerBK to detect actual changes (not initial mount)
  const prevCustomerBKRef = useRef<Undef<string>>(undefined);
  const isFirstRenderRef = useRef(true);

  const lastFetchedCustomerIDRef = useRef<Undef<number>>(undefined);

  // Clear commodities when customer changes (not on initial mount)
  useEffect(() => {
    // Skip on initial mount - don't clear commodities that were loaded with the form
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevCustomerBKRef.current = customerBK;
      return;
    }

    // Only clear if customerBK actually changed
    if (customerBK && prevCustomerBKRef.current !== customerBK) {
      // Remove all commodities when customer changes
      replace([]);
      // Also clear the dropdown selection
      setSelectedCommodityID(null);
      setSelectedCommodityValue(null);
      setShowCommodityDropdown(false);
    }
    // Update ref for next comparison
    prevCustomerBKRef.current = customerBK;
  }, [customerBK, replace]);

  const handleRefreshCommodities = async () => {
    // Always require customerID for refresh to avoid unnecessary API calls for all customers
    if (!tmsID || !customerID) {
      toast({
        description: 'Please select a customer to refresh commodities.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoadingCommodities(true);

    // Always pass customerID to ensure only commodities for this customer are refreshed
    const res = await getCommodities(tmsID, true, false, customerID);
    if (res.isOk()) {
      setCommodities(res.value.commodityList);
      toast({
        description: 'Successfully refreshed commodity list.',
        variant: 'success',
      });
      setIsLoadingCommodities(false);
      return;
    }
    handleCommodityOrLocationFetchError(res.error, 'commodities', toast, {
      isManualRefresh: true,
    });
    setIsLoadingCommodities(false);
  };

  const handleCommoditySearch = async (
    _field: GenericCompanySearchableFields,
    value: string
  ) => {
    // Always return all commodities if no search term or search is too short
    // This ensures options are visible when dropdown opens
    if (!value || value.length < 2) {
      return mapCommoditiesToAntdOptions(commodities);
    }

    // Frontend filtering - search by commodity description, commodity name, or NMFC
    const filtered = (commodities || []).filter((comm) => {
      const searchTerm = value.toLowerCase();
      const commodityDescription = (
        comm.commodityDescription || ''
      ).toLowerCase();
      const commodityName = (comm.commodity || '').toLowerCase();
      const nmfc = (comm.nmfcNumber || comm.nmfc || '').toLowerCase();

      return (
        commodityDescription.includes(searchTerm) ||
        commodityName.includes(searchTerm) ||
        nmfc.includes(searchTerm)
      );
    });

    return mapCommoditiesToAntdOptions(filtered);
  };

  const handleAddItem = () => {
    // Show dropdown to optionally pre-fill from existing commodities
    setShowCommodityDropdown(true);
  };

  const handleAddCommodity = async () => {
    // If no commodity selected, add empty item
    if (!selectedCommodityID) {
      append(createNewCommodity({ dimensionUnit: 'inches' }));
      setShowCommodityDropdown(false);
      setSelectedCommodityID(null);
      setSelectedCommodityValue(null);
      return;
    }

    if (!tmsID) return;

    // Find the selected commodity
    const selectedCommodity = commodities?.find(
      (c) => c.externalTMSID === selectedCommodityID
    );

    if (!selectedCommodity) {
      toast({
        description: 'Commodity not found.',
        variant: 'destructive',
      });
      return;
    }

    // Check if details need to be fetched
    if (!selectedCommodity.hasDetailsFetched) {
      setIsLoadingCommodityDetails((prev) => ({
        ...prev,
        [selectedCommodityID]: true,
      }));

      const res = await getCommodityDetails(
        selectedCommodityID,
        tmsID,
        customerID
      );
      setIsLoadingCommodityDetails((prev) => ({
        ...prev,
        [selectedCommodityID]: false,
      }));

      if (res.isOk()) {
        const detailedCommodity = res.value.commodity;
        // Update commodities list with fetched details (in-place update to avoid duplicates)
        const updatedCommodities = (commodities || []).map((c) => {
          if (c.externalTMSID === selectedCommodityID) {
            // Merge fetched details with existing commodity to preserve all fields
            return {
              ...c,
              ...detailedCommodity,
              hasDetailsFetched: true,
            };
          }
          return c;
        });
        setCommodities(updatedCommodities);

        // Add to form with full details
        addCommodityToForm(detailedCommodity);
      } else {
        toast({
          description: res.error.message || 'Error fetching commodity details.',
          variant: 'destructive',
        });
        return;
      }
    } else {
      // Details already available, add directly
      addCommodityToForm(selectedCommodity);
    }

    // Clear selection and reset dropdown
    setSelectedCommodityID(null);
    setSelectedCommodityValue(null);
    setShowCommodityDropdown(false);
  };

  const addCommodityToForm = (commodity: TMSCommodity) => {
    const newCommodityFormData = createNewCommodity({
      dimensionUnit: 'inches',
    });
    newCommodityFormData.description =
      commodity.commodityDescription || commodity.commodity || '';
    newCommodityFormData.nmfc = commodity.nmfcNumber || commodity.nmfc || '';
    if (commodity.freightClassId != null) {
      newCommodityFormData.freightClassId = commodity.freightClassId;
    }
    newCommodityFormData.weightTotal = commodity.weightAmount || 0;
    // Map weightUnitId to weightUnit string: 0 = kg, 1 = lbs
    if (
      commodity.weightUnitId !== undefined &&
      commodity.weightUnitId !== null
    ) {
      newCommodityFormData.weightUnit =
        commodity.weightUnitId === 0 ? 'kg' : 'lbs';
    } else {
      newCommodityFormData.weightUnit = commodity.weightUnit || 'lbs'; // Default to lbs
    }
    newCommodityFormData.length = commodity.length || 0;
    newCommodityFormData.width = commodity.width || 0;
    newCommodityFormData.height = commodity.height || 0;
    // Map dimUnitId to dimensionUnit string
    // 0 = inches, 1 = feet
    if (commodity.dimUnitId !== undefined && commodity.dimUnitId !== null) {
      newCommodityFormData.dimensionUnit =
        commodity.dimUnitId === 0 ? 'inches' : 'feet';
    } else {
      // Fallback to dimUnit string if available, otherwise default to inches
      if (commodity.dimUnit === 'feet' || commodity.dimUnit === 'ft') {
        newCommodityFormData.dimensionUnit = 'feet';
      } else {
        newCommodityFormData.dimensionUnit = 'inches'; // Default to inches
      }
    }
    newCommodityFormData.handlingQuantity = commodity.handlingUnitCount || 1;
    newCommodityFormData.quantity = commodity.pieceCount || 1;
    newCommodityFormData.hazardousMaterial =
      commodity.hazardousMaterial || false;
    newCommodityFormData.stackable = commodity.stackable || false;

    // Map HazMat fields if present
    if (commodity.hazmatClassId) {
      newCommodityFormData.hazmatClassId = commodity.hazmatClassId;
    }
    if (commodity.hazmatGroupId) {
      newCommodityFormData.hazmatGroupId = commodity.hazmatGroupId;
    }
    if (commodity.hazmatPrefixId) {
      newCommodityFormData.hazmatPrefixId = commodity.hazmatPrefixId;
    }
    if (commodity.hazmatCode) {
      newCommodityFormData.hazmatCode = commodity.hazmatCode;
    }
    if (commodity.hazmatChemicalName) {
      newCommodityFormData.hazmatChemicalName = commodity.hazmatChemicalName;
    }
    if (commodity.hazmatEmergencyContactNumber) {
      newCommodityFormData.hazmatEmergencyContact =
        commodity.hazmatEmergencyContactNumber;
    }

    if (commodity.pieceCount) {
      newCommodityFormData.totalPieces = {
        val: commodity.pieceCount,
        unit: Unit.Piece,
      };
    }
    if (commodity.weightAmount) {
      // Map weightUnit to Unit enum (weightUnit is already set above based on weightUnitId)
      const weightUnit =
        newCommodityFormData.weightUnit === 'kg' ? Unit.Kg : Unit.Pounds;
      newCommodityFormData.grossWeight = {
        val: commodity.weightAmount,
        unit: weightUnit,
      };
      newCommodityFormData.netWeight = {
        val: commodity.weightAmount,
        unit: weightUnit,
      };
    }

    append(mapCommodityDefaults([newCommodityFormData]));
  };

  const handleCommodityCreated = async (newCommodity: TMSCommodity) => {
    // Add the newly created commodity to the list (backend already fetched full details)
    // No need to force refresh all commodities - just add this one to the list
    if (newCommodity.externalTMSID) {
      setCommodities((prev) => {
        if (!prev) {
          return [newCommodity];
        }
        // Check if commodity already exists (shouldn't happen, but safe check)
        const exists = prev.some(
          (c) => c.externalTMSID === newCommodity.externalTMSID
        );
        if (exists) {
          // Update existing commodity with new data
          return prev.map((c) =>
            c.externalTMSID === newCommodity.externalTMSID ? newCommodity : c
          );
        }
        // Add new commodity to the list
        return [...prev, newCommodity];
      });

      toast({
        description:
          'Commodity created successfully. You can now select it from the dropdown.',
        variant: 'success',
      });

      // Optionally auto-select the newly created commodity
      setSelectedCommodityID(newCommodity.externalTMSID);
      setSelectedCommodityValue({
        value: newCommodity.externalTMSID,
        label:
          newCommodity.commodityDescription || newCommodity.commodity || '',
        data: newCommodity,
      });
    }
  };

  // No customer selected state
  if (!customerBK) {
    return (
      <div className='text-center py-8 text-neutral-500'>
        <Package className='h-12 w-12 mx-auto mb-2 opacity-50' />
        <Typography variant='body' className='text-neutral-600'>
          Please select a customer to add shipment items
        </Typography>
      </div>
    );
  }

  // No commodities found for customer - still allow adding empty items
  const hasCommodities = commodities && commodities.length > 0;

  return (
    <div className='space-y-4'>
      {/* Loading state when fetching commodities */}
      {isLoadingCommodities && (
        <div className='text-center py-6 text-neutral-500'>
          <Loader2 className='h-6 w-6 mx-auto mb-2 animate-spin text-info-600' />
          <Typography variant='body-sm' className='text-neutral-600'>
            Loading commodities...
          </Typography>
        </div>
      )}

      {/* Add Action Button - hide when loading */}
      {!isLoadingCommodities && (
        <AddActionButton
          onClick={handleAddItem}
          buttonText='Add Item'
          descriptionText='Add item(s) for the shipment'
          buttonNamePosthog={ButtonNamePosthog.CreateCommodity}
          className='mb-2'
        />
      )}

      {/* Commodity Dropdown (shown when adding item) */}
      {showCommodityDropdown && !isLoadingCommodities && (
        <div className='border border-neutral-200 rounded-lg p-4 mb-4'>
          <Typography variant='body-sm' className='text-neutral-600 mb-4'>
            {hasCommodities
              ? 'Optionally select a commodity to pre-fill details, or add an empty item:'
              : 'No commodities available for this customer. You can add an empty item and fill in details manually:'}
          </Typography>
          <div className='mb-4'>
            <RHFDebounceSelect
              name={'commoditySearch' as any}
              label='Commodity (Optional)'
              control={control}
              errors={errors}
              data={commodities}
              isLoading={isLoadingCommodities}
              fetchOptions={handleCommoditySearch}
              mapOptions={mapCommoditiesToAntdOptions}
              showSearchParamDropdown={false}
              placeholder='Search by description or NMFC'
              required={false}
              disabled={!hasCommodities}
              showRefreshButton={true}
              refreshHandler={handleRefreshCommodities}
              useSimpleOptionRender={true}
              customActionButtons={
                <button
                  type='button'
                  onClick={() => setIsCommodityCreatorOpen(true)}
                  className='shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors cursor-pointer'
                  title='Create new commodity'
                >
                  <Plus className='h-4 w-4' />
                </button>
              }
              controllingParentValue={
                selectedCommodityValue?.value ||
                selectedCommodityID ||
                undefined
              }
              valueRenderer={(selectedOption: any) => {
                // Use the label from the option if available, otherwise try to get from commodity
                if (!selectedOption) {
                  return { label: '', value: '' };
                }
                const label =
                  selectedOption?.label ||
                  selectedOption?.commodity ||
                  selectedOption?.commodityDescription ||
                  selectedOption?.value ||
                  '';
                return {
                  label,
                  value:
                    selectedOption?.value ||
                    selectedOption?.externalTMSID ||
                    '',
                };
              }}
              parentOnChange={(value: any) => {
                if (!value) {
                  setSelectedCommodityID(null);
                  setSelectedCommodityValue(null);
                  return;
                }
                // value is the full option object from mapCommoditiesToAntdOptions
                // Format: { value: externalTMSID, label: commodity, data: TMSCommodity }
                const commodityID =
                  value?.value || value?.externalTMSID || null;
                setSelectedCommodityID(commodityID);
                // Store the option object as-is for display (it already has the correct format)
                setSelectedCommodityValue(value);
              }}
            />
          </div>
          <Flex gap='sm' justify='end' className='mt-4'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              buttonNamePosthog={ButtonNamePosthog.CancelLocationCreation}
              onClick={() => {
                setShowCommodityDropdown(false);
                setSelectedCommodityID(null);
                setSelectedCommodityValue(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type='button'
              variant='default'
              size='sm'
              buttonNamePosthog={ButtonNamePosthog.CreateCommodity}
              onClick={handleAddCommodity}
              disabled={
                isLoadingCommodityDetails[selectedCommodityID || ''] === true
              }
            >
              {isLoadingCommodityDetails[selectedCommodityID || '']
                ? 'Loading...'
                : selectedCommodityID
                  ? 'Add Selected Item'
                  : 'Add Empty Item'}
            </Button>
          </Flex>
        </div>
      )}

      {/* Validation message when no shipment items have been added */}
      {fields.length === 0 && !isLoadingCommodities && (
        <Typography variant='body-xs' className='text-error-500'>
          At least one shipment item required
        </Typography>
      )}

      {/* Display Added Commodities */}
      {fields.length > 0 && (
        <AnimatePresence>
          <div className='flex flex-col gap-4 mt-4'>
            {fields.map((field, index) => (
              <CommodityItemCard
                key={field.id}
                index={index}
                formMethods={formMethods}
                onRemove={remove}
              />
            ))}
          </div>
        </AnimatePresence>
      )}

      {/* Total Shipment Value (USD) - shown after all commodities */}
      {fields.length > 0 && (
        <div className='mt-6 pt-4 border-t border-neutral-200'>
          <RHFTextInput
            name='declaredValueUSD'
            label='Total Shipment Value (USD)'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            placeholder='100000'
          />
        </div>
      )}

      {/* Commodity Creator Dialog */}
      {tmsID && (
        <CommodityCreatorDialog
          isOpen={isCommodityCreatorOpen}
          onClose={() => setIsCommodityCreatorOpen(false)}
          onCommodityCreated={handleCommodityCreated}
          onCommoditiesRefreshed={(customerID) => {
            // Update the last fetched customer ID to prevent race conditions on search results
            lastFetchedCustomerIDRef.current = customerID;
            // Pass carrierType to maintain consistent filtering with the main fetch flow
            getCommodities(tmsID, false, false, customerID, carrierType).then(
              (res) => {
                // Only update commodities if this response is for the most recent customer
                if (
                  res.isOk() &&
                  lastFetchedCustomerIDRef.current === customerID
                ) {
                  setCommodities(res.value.commodityList);
                }
              }
            );
          }}
          tmsID={tmsID}
          customerBK={customerBK}
          initialCustomers={initialCustomers}
          initialIsLoadingCustomers={initialIsLoadingCustomers}
        />
      )}
    </div>
  );
}
